import React, { useState } from 'react';

import { BasicModal } from '@DesignSystem/modals';
import { TextInput } from '@ds';
import { usePanelLibraryTemplates } from '@mpm-druid/api';
import {
  LibraryPanelTemplate,
  UnsavedLibraryPanelTemplate
} from '@mpm-druid/types';

import styles from './PythonTemplateSelect.module.scss';

type PythonTemplateCreateModalProps = {
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (name: string) => void;
  template: UnsavedLibraryPanelTemplate | LibraryPanelTemplate | null;
};

export function PythonTemplateCreateModal({
  isSubmitting,
  onClose,
  onSubmit,
  template = null
}: PythonTemplateCreateModalProps) {
  const { data: templates } = usePanelLibraryTemplates();

  const [newTemplateName, setNewTemplateName] = useState(
    template?.name || 'New Python Template'
  );

  const handleSubmitCreateTemplate = async (e: React.SyntheticEvent) => {
    e.preventDefault();

    if (isSubmitting || isNameInvalid) return;

    onSubmit(newTemplateName);
  };

  const safeClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const templateNames = templates?.map(t => t.name) || [];
  const isNameTaken =
    template?.name !== newTemplateName &&
    templateNames.includes(newTemplateName);
  const isNameInvalid = !newTemplateName || isNameTaken;

  return (
    <BasicModal
      content={
        <form
          className={styles.formContainer}
          onSubmit={handleSubmitCreateTemplate}
        >
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Name of the template</span>
            <TextInput
              autoFocus
              data-test="template-select-name-field"
              invalid={isNameInvalid}
              maxLength={100}
              onChange={e => setNewTemplateName(e.target.value)}
              placeholder="Enter name"
              value={newTemplateName}
              helperText={
                isNameTaken
                  ? 'Name is already taken'
                  : newTemplateName
                  ? undefined
                  : 'Name is required'
              }
            />
          </div>
        </form>
      }
      dataTest="python-template-create-modal"
      onClose={safeClose}
      open
      isPrimaryButtonDisabled={isSubmitting || isNameInvalid}
      isPrimaryBtnLoading={isSubmitting}
      onPrimaryButtonClick={handleSubmitCreateTemplate}
      primaryButtonText={template ? 'Update' : 'Create'}
      secondaryButtonText="Cancel"
      onSecondaryButtonClick={safeClose}
      title={template ? 'Update template' : 'Create new template'}
    />
  );
}
